import { parseSRT } from './srtParser.js';

/**
 * Manages loading, parsing, and displaying subtitles in sync with an AudioManager instance.
 */
export class SubtitleManager {
    /**
     * @param {import('../audio/AudioManager.js').default} audioManager - The AudioManager instance to sync with.
     * @param {HTMLElement} containerElement - The HTML element to display subtitles in.
     */
    constructor(audioManager, containerElement) {
        if (!audioManager || !containerElement) {
            throw new Error('SubtitleManager requires an AudioManager instance and a container element.');
        }
        /** @type {import('../audio/AudioManager.js').default} */
        this.audioManager = audioManager; 
        this.containerElement = containerElement;
        this.subtitles = [];
        this.currentSubtitleIndex = -1;
        this.timeOffset = 0; // Initialize time offset

        // No longer need to bind handleTimeUpdate as an event handler
        // this.handleTimeUpdate = this.handleTimeUpdate.bind(this);
    }

    /**
     * Loads subtitles from an SRT file URL.
     * Parses the content and prepares for display.
     * @param {string} srtUrl - The URL of the .srt file.
     */
    async loadSubtitles(srtUrl) {
        try {
            const response = await fetch(srtUrl);
            if (!response.ok) {
                throw new Error(`Failed to fetch SRT file: ${response.statusText}`);
            }
            const srtContent = await response.text();
            this.subtitles = parseSRT(srtContent);
            console.log(`Loaded ${this.subtitles.length} subtitles.`);
            this.reset(); // Reset state after loading
            // No need to call start() here, synchronization is driven externally
        } catch (error) {
            console.error('Error loading or parsing subtitles:', error);
            this.subtitles = [];
            this.reset();
        }
    }

     /**
      * Loads subtitles from raw SRT content string.
      * Parses the content and prepares for display.
      * @param {string} srtContent - The raw SRT content.
      */
     loadSubtitlesFromString(srtContent) {
         try {
             this.subtitles = parseSRT(srtContent);
             console.log(`Loaded ${this.subtitles.length} subtitles from string.`);
             this.reset(); // Reset state after loading
         } catch (error) {
             console.error('Error parsing subtitles from string:', error);
             this.subtitles = [];
             this.reset();
         }
     }

    // start() and stop() methods are removed as synchronization is now driven externally
    // start() { ... }
    // stop() { ... }

    /**
     * Resets the subtitle display and internal state.
     */
    reset() {
         this.currentSubtitleIndex = -1;
         this.clearSubtitle();
         // No immediate update needed, external loop will call handleTimeUpdate
         // this.handleTimeUpdate(); 
    }

    /**
     * Completely clears all subtitles and resets state.
     * Useful when loading new content.
     */
    clearAll() {
        this.subtitles = [];
        this.reset();
        console.log('SubtitleManager: All subtitles cleared.');
    }

    /**
     * Clears the subtitle container text and hides it.
     */
    clearSubtitle() {
        this.containerElement.innerHTML = '';
        this.containerElement.classList.remove('visible');
    }

    /**
     * Adjusts the subtitle timing offset.
     * @param {number} amount - The amount to adjust the offset by (in seconds). Positive values delay subtitles, negative values advance them.
     */
    adjustOffset(amount) {
        this.timeOffset += amount;
        // Optional: Add console log or update UI element showing the offset
        console.log(`Subtitle offset adjusted by ${amount}s. New offset: ${this.timeOffset.toFixed(1)}s`);
        // We might need to force an update check if the offset change could immediately change the current subtitle
        this.update();
        // No longer returning the offset
    }

    /**
     * Updates the subtitle display based on the current time from AudioManager.
     * This method should be called repeatedly (e.g., in the animation loop).
     */
    update() { // Renamed from handleTimeUpdate to reflect its new role
        if (!this.subtitles || this.subtitles.length === 0 || !this.audioManager.isPlaying()) {
            // If no subtitles or audio is not playing, ensure subtitle is hidden
            if (this.currentSubtitleIndex !== -1 || this.containerElement.classList.contains('visible')) {
                 this.clearSubtitle();
                 this.currentSubtitleIndex = -1;
            }
            return; 
        }

        const currentTime = this.audioManager.getCurrentPlaybackTime();
        const adjustedTime = currentTime - this.timeOffset; // Apply the offset
        
        // Check if the current subtitle is still active
        if (this.currentSubtitleIndex !== -1) {
            const currentSub = this.subtitles[this.currentSubtitleIndex];
            if (adjustedTime >= currentSub.startTime && adjustedTime <= currentSub.endTime) {
                return; // Still displaying the correct subtitle, do nothing
            }
        }

        // Find the new active subtitle
        let newSubtitleIndex = -1;
        for (let i = 0; i < this.subtitles.length; i++) {
            const sub = this.subtitles[i];
            if (adjustedTime >= sub.startTime && adjustedTime <= sub.endTime) {
                newSubtitleIndex = i;
                break;
            }
        }

        // Update display only if the active subtitle has changed
        if (newSubtitleIndex !== this.currentSubtitleIndex) {
            if (newSubtitleIndex !== -1) {
                this.displaySubtitle(this.subtitles[newSubtitleIndex].text);
            } else {
                this.clearSubtitle(); // No active subtitle found for this time
            }
            this.currentSubtitleIndex = newSubtitleIndex;
        }
    }

    /**
     * Updates the container element with the subtitle text and makes it visible.
     * @param {string} text - The subtitle text to display.
     */
    displaySubtitle(text) {
        // Replace newline characters with <br> tags for HTML rendering
        this.containerElement.innerHTML = text.replace(/\n/g, '<br>');
        this.containerElement.classList.add('visible');
    }
} 